import React, { useMemo, useState, useCallback, useRef } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { 
  getFileIconByExtension, 
  formatFileSize, 
  isImageFile, 
  getFileTypeColor,
  getStatusColor,
  getStatusText 
} from '@/constants/utils/files';
import { BORDER_RADIUS } from '@/constants/config';

const createStyles = (colors, isHovered, isAltBGColor) => StyleSheet.create({
  container: {
    borderRadius: BORDER_RADIUS.medium,
    overflow: 'hidden',
    backgroundColor: isHovered ? (isAltBGColor ? colors.solid_color_secondary_dark : colors.solid_color_tertiary) : (isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block),
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    transition: 'all 0.2s ease',
  },
  imageContainer: {
    width: '100%',
    aspectRatio: 1,
    backgroundColor: colors.solid_color_block,
    overflow: 'hidden',
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  iconContainer: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBackground: {
    width: 64,
    height: 64,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoContainer: {
    padding: 12,
    gap: 4,
    minWidth: 0,
  },
  fileName: {
    lineHeight: 18,
    maxWidth: '100%',
    ...(Platform.OS === 'web' ? {
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    } : {}),
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  metaLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  selectCheckbox: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 30,
    height: 30,
    borderRadius: 6,
    backgroundColor: colors.solid_color_block,
    borderWidth: 2,
    borderColor: colors.border_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  selectCheckboxHovered: {
    backgroundColor: colors.solid_color_primary,
  },
  selectCheckboxSelected: {
    backgroundColor: colors.accent_color_primary,
    borderColor: colors.accent_color_primary,
  },
  selectCheckboxDisabled: {
    opacity: 0.4,
  },
  hoverOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    opacity: 0,
  },
  hoverOverlayVisible: {
    opacity: 1,
  },
});

const FileCard = ({ 
  file, 
  onPress, 
  onSelect,
  isSelected = false,
  selectionDisabled = false,
  showStatus = true,
}) => {
  const { colors, theme } = useTheme();
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isHoveringSelectCheckbox, setIsHoveringSelectCheckbox] = useState(false);
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(() => createStyles(colors, isHovered, isAltBGColor), [colors, isHovered, isAltBGColor]);
  
  const fileTypeColor = useMemo(() => getFileTypeColor(file.extension || file.mimeType), [file.extension, file.mimeType]);
  const statusColor = useMemo(() => getStatusColor(file.processingStatus?.status || 'completed'), [file.processingStatus?.status]);
  const isImage = useMemo(() => isImageFile(file.extension), [file.extension]);

  const handlePress = useCallback(() => {
    // AppKit keeps the old text field focused after a mouse click unless the
    // card takes focus before the preview records its return target.
    if (Platform.OS === 'macos') cardRef.current?.focus?.();
    onPress?.(file);
  }, [onPress, file]);

  const handleSelect = useCallback((e) => {
    e.stopPropagation();
    if (!selectionDisabled || isSelected) {
      onSelect?.(file);
    }
  }, [selectionDisabled, isSelected, onSelect, file]);

  const handleHoverIn = useCallback(() => setIsHovered(true), []);
  const handleHoverOut = useCallback(() => setIsHovered(false), []);
  const handleCheckboxHoverIn = useCallback(() => setIsHoveringSelectCheckbox(true), []);
  const handleCheckboxHoverOut = useCallback(() => setIsHoveringSelectCheckbox(false), []);
  const Container = Pressable;

  const renderThumbnail = () => {
    if (isImage && file.thumbnailPath) {
      return (
        <Image
          source={{ uri: file.thumbnailPath }}
          style={styles.image}
          contentFit="cover"
          placeholder={file.blurhash ? { blurhash: file.blurhash } : undefined}
          transition={200}
        />
      );
    }

    return (
      <View style={styles.iconContainer}>
        <View style={[styles.iconBackground, { backgroundColor: fileTypeColor + '20' }]}>
          <Icon 
            name={getFileIconByExtension(file.extension)} 
            size={32} 
            color={fileTypeColor} 
          />
        </View>
      </View>
    );
  };

  return (
    <Container
      ref={cardRef}
      accessibilityRole="button"
      accessibilityLabel={file.name}
      style={styles.container}
      onPress={handlePress}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
    >
      <View style={styles.imageContainer}>
        {renderThumbnail()}
        
        {/* Hover overlay */}
        <View style={[
          styles.hoverOverlay, 
          (Platform.OS === 'web' && isHovered) && styles.hoverOverlayVisible
        ]} pointerEvents="none" />
        
        {/* Selection checkbox - always visible */}
        {onSelect && (
          <Pressable 
            accessibilityRole="checkbox"
            accessibilityLabel={file.name}
            accessibilityState={{ checked: isSelected, disabled: selectionDisabled && !isSelected }}
            disabled={selectionDisabled && !isSelected}
            style={[
              styles.selectCheckbox,
              isHoveringSelectCheckbox && !isSelected && styles.selectCheckboxHovered,
              isSelected && styles.selectCheckboxSelected,
              selectionDisabled && !isSelected && styles.selectCheckboxDisabled,
            ]}
            onPress={handleSelect}
            onHoverIn={handleCheckboxHoverIn}
            onHoverOut={handleCheckboxHoverOut}
          >
            {isSelected ? (
              <Icon name="check" size={12} color="#fff" />
            ) : (
              <Icon 
                name="plus" 
                size={12} 
                color={isHoveringSelectCheckbox ? colors.text_color_primary : colors.text_color_tertiary} 
              />
            )}
          </Pressable>
        )}
      </View>

      <View style={styles.infoContainer}>
        <Text 
          size="s" 
          weight="medium" 
          numberOfLines={2} 
          ellipsizeMode="tail"
          style={styles.fileName}
        >
          {file.name}
        </Text>
        
        <View style={styles.metaRow}>
          <View style={styles.metaLeft}>
            <Text size="xs" color="tertiary">
              {formatFileSize(file.sizeInBytes)}
            </Text>
            {showStatus && file.processingStatus?.status && (
              <>
                <Text size="xs" color="tertiary">•</Text>
                <View style={[styles.statusDot, { backgroundColor: statusColor.text }]} />
              </>
            )}
          </View>
        </View>
      </View>
    </Container>
  );
};

export default React.memo(FileCard);
